import React, { useRef, useEffect } from "react";
import type { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

interface EditableTextProps {
  value: string;
  onUpdate: (newValue: string) => void;
  style?: React.CSSProperties;
  className?: string;
  multiline?: boolean;
}

const EditableText = ({
  value,
  onUpdate,
  style,
  className,
  multiline = false,
}: EditableTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current && textRef.current.innerText !== value) {
      textRef.current.innerText = value;
    }
  }, [value]);

  const handleBlur = () => {
    if (textRef.current) {
      const newValue = textRef.current.innerText.trim();
      if (newValue !== value) {
        onUpdate(newValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      textRef.current?.blur();
    }
  };

  return (
    <div
      ref={textRef}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        outline: "none",
        cursor: "text",
        transition: "background-color 0.2s",
        ...style,
      }}
      className={className || `hover:bg-blue-50/50 focus:bg-blue-100/50 transition-colors rounded-sm px-1 -mx-1`}
    >
      {value}
    </div>
  );
};

interface ProspectorTemplateProps {
  data: CVData;
  onUpdate?: (newData: CVData) => void;
}

const SectionHeading = ({ children, onUpdate, align = "left" }: { children: string; onUpdate?: (val: string) => void; align?: "left" | "right" }) => (
  <div style={{ marginBottom: "8px" }}>
    <div
      style={{
        color: "#666666",
        fontSize: "12px",
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        textAlign: align,
        paddingBottom: "6px",
        borderBottom: "1px solid #e0e0e0"
      }}
    >
      {onUpdate ? (
        <EditableText
          value={children}
          onUpdate={onUpdate}
          style={{ 
            backgroundColor: "transparent",
            color: "#666666",
            display: "inline-block",
          }}
          className="hover:bg-gray-100 focus:bg-gray-100 p-0 m-0"
        />
      ) : (
        <span>{children}</span>
      )}
    </div>
  </div>
);

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const ProspectorTemplate = React.forwardRef<HTMLDivElement, ProspectorTemplateProps>(
  ({ data, onUpdate }, ref) => {
    
    const debouncedUpdate = React.useCallback(
      debounce((newData: CVData) => {
        if (onUpdate) onUpdate(newData);
      }, 300),
      [onUpdate]
    );

    const updateHeader = (key: "name" | "jobTitle", val: string) => {
      debouncedUpdate({ ...data, [key]: val });
    };

    const updateSectionName = (key: keyof NonNullable<CVData["sectionNames"]>, val: string) => {
      debouncedUpdate({ 
        ...data, 
        sectionNames: { ...(data.sectionNames || {}), [key]: val } as any 
      });
    };

    const updateBasicInfo = (index: number, val: string) => {
      const newInfo = [...data.basicInfo];
      newInfo[index] = val;
      debouncedUpdate({ ...data, basicInfo: newInfo });
    };

    const updateProfile = (val: string) => {
      debouncedUpdate({ ...data, profile: val });
    };

    const updateSkill = (index: number, val: string) => {
      const newSkills = [...data.keySkills];
      newSkills[index] = val;
      debouncedUpdate({ ...data, keySkills: newSkills });
    };

    const updateWorkEntry = (index: number, field: keyof WorkEntry, val: any) => {
      const newExperience = [...data.workExperience];
      newExperience[index] = { ...newExperience[index], [field]: val };
      debouncedUpdate({ ...data, workExperience: newExperience });
    };

    const updateWorkBullet = (entryIndex: number, bulletIndex: number, val: string) => {
      const newExperience = [...data.workExperience];
      const newBullets = [...newExperience[entryIndex].bullets];
      newBullets[bulletIndex] = val;
      newExperience[entryIndex] = { ...newExperience[entryIndex], bullets: newBullets };
      debouncedUpdate({ ...data, workExperience: newExperience });
    };

    const updateEducationEntry = (index: number, field: keyof EducationEntry, val: any) => {
      const newEducation = [...data.education];
      newEducation[index] = { ...newEducation[index], [field]: val };
      debouncedUpdate({ ...data, education: newEducation });
    };

    const updateEducationNote = (entryIndex: number, noteIndex: number, val: string) => {
      const newEducation = [...data.education];
      const newNotes = [...newEducation[entryIndex].notes];
      newNotes[noteIndex] = val;
      newEducation[entryIndex] = { ...newEducation[entryIndex], notes: newNotes };
      debouncedUpdate({ ...data, education: newEducation });
    };

    const updateInterest = (index: number, val: string) => {
      const newInterests = [...data.interests];
      newInterests[index] = val;
      debouncedUpdate({ ...data, interests: newInterests });
    };

    const sNames = data.sectionNames || {
      basicInfo: "Contact",
      profile: "Professional Summary",
      keySkills: "Skills",
      workExperience: "Work Experience",
      education: "Education",
      interests: "Languages", // Re-purposing interests as languages based on image
      references: "References"
    };

    return (
      <div
        ref={ref}
        id="cv-template-root"
        style={{
          backgroundColor: "#ffffff",
          color: "#333333",
          fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          width: "794px",
          /* Use 1120px (slightly less than A4 1122px) to ensure sub-pixel rounding doesn't trigger a blank 2nd page */
          minHeight: "1120px",
          boxSizing: "border-box",
          margin: "0 auto",
          position: "relative",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          textRendering: "optimizeLegibility",
          display: "table",
          tableLayout: "fixed"
        }}
      >
        {/* CSS Injection to fix the PDF generator wrapper and center the content */}
        <style dangerouslySetInnerHTML={{ __html: `
          /* Force the generator's hidden wrapper to have zero padding so our 794px template fits perfectly */
          body > div[style*="top: -99999px"] {
            padding: 0 !important;
          }
          /* Adjust internal padding specifically for the PDF download to center text perfectly 
             We use 50px on both sides to match the website's effective inner width (794 - 148 = 646px)
          */
          body > #cv-template-root .prospector-sidebar {
            padding-left: 50px !important;
          }
          body > #cv-template-root .prospector-content {
            padding-right: 50px !important;
          }
        `}} />

        {/* LEFT COLUMN (Sidebar) */}
          <div 
          className="prospector-sidebar" 
          style={{ 
            display: "table-cell",
            width: "238px", 
            verticalAlign: "top",
            padding: "24px 16px 48px 48px", 
            backgroundColor: "#ffffff" 
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <EditableText
              value={data.name.toUpperCase()}
              onUpdate={(val) => updateHeader("name", val)}
              multiline
              className="prospector-name hover:bg-blue-50/50 focus:bg-blue-100/50 transition-colors rounded-sm px-1 -mx-1"
              style={{
                fontSize: "28px",
                fontWeight: 300,
                color: "#111111",
                letterSpacing: "0.5px",
                lineHeight: 1.1,
                display: "block",
                marginBottom: "8px",
                wordBreak: "normal"
              }}
            />
            <EditableText
              value={data.jobTitle.toUpperCase()}
              onUpdate={(val) => updateHeader("jobTitle", val)}
              className="prospector-title hover:bg-blue-50/50 focus:bg-blue-100/50 transition-colors rounded-sm px-1 -mx-1"
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#777777",
                letterSpacing: "1px",
                display: "block",
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("basicInfo", val)}>
              {sNames.basicInfo}
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {data.basicInfo.map((line, i) => (
                <EditableText
                  key={i}
                  value={line}
                  onUpdate={(val) => updateBasicInfo(i, val)}
                  style={{ fontSize: "11px", color: "#444444", lineHeight: "1.4", fontWeight: 400 }}
                />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("keySkills", val)}>
              {sNames.keySkills}
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {data.keySkills.map((skill, i) => (
                <div key={i} style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                  <span style={{ fontSize: "11px", color: "#444" }}>•</span>
                  <EditableText
                    value={skill}
                    onUpdate={(val) => updateSkill(i, val)}
                    style={{ fontSize: "11px", color: "#444444", flex: 1, fontWeight: 400 }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("education", val)}>
              {sNames.education}
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {data.education.map((entry, i) => (
                <div key={i} className="prospector-education-entry" style={{ display: "flex", flexDirection: "column" }}>
                  <EditableText
                    value={entry.qualification}
                    onUpdate={(val) => updateEducationEntry(i, "qualification", val)}
                    style={{ fontWeight: 600, fontSize: "11px", color: "#333", marginBottom: "2px" }}
                  />
                  <EditableText
                    value={entry.institution}
                    onUpdate={(val) => updateEducationEntry(i, "institution", val)}
                    style={{ fontSize: "11px", color: "#555", marginBottom: "2px" }}
                  />
                  <EditableText
                    value={entry.dates}
                    onUpdate={(val) => updateEducationEntry(i, "dates", val)}
                    style={{ fontSize: "10px", color: "#888" }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("interests", val)}>
              {sNames.interests}
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {data.interests.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                  <span style={{ fontSize: "11px", color: "#444" }}>•</span>
                  <EditableText
                    value={item}
                    onUpdate={(val) => updateInterest(i, val)}
                    style={{ fontSize: "11px", color: "#444444", flex: 1, fontWeight: 400 }}
                  />
                </div>
              ))}
            </div>
          </div>

          {data.references && (
            <div style={{ marginBottom: "24px" }}>
              <SectionHeading onUpdate={(val) => updateSectionName("references", val)}>
                {sNames.references}
              </SectionHeading>
              <div style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                <span style={{ fontSize: "11px", color: "#444" }}>•</span>
                <EditableText
                  value={data.references}
                  onUpdate={(val) => debouncedUpdate({ ...data, references: val })}
                  multiline
                  style={{ fontSize: "11px", color: "#444444", flex: 1, fontWeight: 400 }}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN (Content) */}
        <div 
          className="prospector-content" 
          style={{ 
            display: "table-cell",
            width: "556px", 
            verticalAlign: "top",
            wordWrap: "break-word", 
            overflow: "visible", 
            padding: "24px 52px 48px 24px", 
            backgroundColor: "#ffffff" 
          }}
        >
          <div style={{ marginBottom: "24px", marginTop: "8px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("profile", val)}>
              {sNames.profile}
            </SectionHeading>
            <EditableText
              value={data.profile}
              onUpdate={updateProfile}
              multiline
              style={{
                fontSize: "12px",
                color: "#333333",
                lineHeight: "1.6",
                textAlign: "left",
                fontWeight: 400,
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("workExperience", val)}>
              {sNames.workExperience}
            </SectionHeading>
            {data.workExperience.map((entry, i) => (
              <div key={i} className="prospector-work-entry" style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: "4px"
                  }}
                >
                  <EditableText
                    value={entry.jobTitle.toUpperCase()}
                    onUpdate={(val) => updateWorkEntry(i, "jobTitle", val)}
                    style={{ fontWeight: 600, fontSize: "12px", color: "#333", flex: 1, letterSpacing: "0.5px" }}
                  />
                  <EditableText
                    value={entry.dates}
                    onUpdate={(val) => updateWorkEntry(i, "dates", val)}
                    style={{
                      fontSize: "11px",
                      color: "#777",
                      whiteSpace: "nowrap",
                      marginLeft: "auto",
                      textAlign: "right",
                      fontWeight: 400,
                    }}
                  />
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <EditableText
                    value={entry.company}
                    onUpdate={(val) => updateWorkEntry(i, "company", val)}
                    style={{ fontSize: "12px", color: "#666", display: "block", fontStyle: "italic", fontWeight: 400 }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {entry.bullets.map((b, bi) => (
                    <div key={bi} style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "12px", color: "#333", marginTop: "2px" }}>•</span>
                      <EditableText
                        value={b}
                        onUpdate={(val) => updateWorkBullet(i, bi, val)}
                        multiline
                        style={{ fontSize: "12px", color: "#333", lineHeight: "1.5", flex: 1, fontWeight: 400 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  }
);

ProspectorTemplate.displayName = "ProspectorTemplate";
(ProspectorTemplate as any).templateName = "Prospector";

export default ProspectorTemplate;
